import { useState, useEffect } from "react";
import * as LucideIcons from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/hooks/useLanguage";

interface Category {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  icon: string | null;
  color_from: string;
  color_to: string;
  active: boolean;
  sort_order: number;
  name_translations?: Record<string, string> | null;
}

const CategoriesSection = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryCounts, setCategoryCounts] = useState<{ [key: string]: number }>({});
  const [loading, setLoading] = useState(true);

  // Dynamic icon mapping using all Lucide React icons
  const iconMap: { [key: string]: any } = LucideIcons;

  // Fix specific icon name mappings
  const getIconComponent = (iconName: string | null) => {
    if (!iconName) return LucideIcons.Package;
    
    // Handle specific icon name corrections - use only icons that definitely exist
    const iconMappings: { [key: string]: any } = {
      'Laptop': LucideIcons.Laptop,
      'Shirt': LucideIcons.ShirtIcon || LucideIcons.Shirt,
      'Home': LucideIcons.Home,
      'Baby': LucideIcons.Baby,
      'Car': LucideIcons.Car,
      'Gamepad2': LucideIcons.Gamepad2 || LucideIcons.Gamepad,
      'Book': LucideIcons.BookOpen || LucideIcons.Book,
      'Heart': LucideIcons.Heart,
      'Palette': LucideIcons.Palette,
      'Wine': LucideIcons.Wine || LucideIcons.GlassWater,
      'ShoppingCart': LucideIcons.ShoppingCart,
      'Sparkles': LucideIcons.Sparkles,
    };
    
    const Icon = iconMappings[iconName] || iconMap[iconName];
    
    // Debug: log if icon not found
    if (!Icon) {
      console.warn(`Icon not found: ${iconName}, available keys sample:`, Object.keys(LucideIcons).slice(0, 20));
    }
    
    return Icon || LucideIcons.Package;
  };

  const getColorForCategory = (category: Category): string => {
    // Map category colors to actual Tailwind classes
    const colorMap: { [key: string]: string } = {
      'blue-500': 'from-blue-500 to-blue-600',
      'pink-500': 'from-pink-500 to-pink-600',
      'green-500': 'from-green-500 to-green-600',
      'purple-500': 'from-purple-500 to-purple-600',
      'orange-500': 'from-orange-500 to-orange-600',
      'red-500': 'from-red-500 to-red-600',
      'indigo-500': 'from-indigo-500 to-indigo-600',
      'rose-500': 'from-rose-500 to-rose-600',
      'violet-500': 'from-violet-500 to-violet-600',
      'amber-500': 'from-amber-500 to-amber-600',
      'emerald-500': 'from-emerald-500 to-emerald-600',
    };
    
    // Use color_from from DB to get the gradient
    if (category.color_from && colorMap[category.color_from]) {
      return colorMap[category.color_from];
    }
    
    // Fallback to blue gradient
    return 'from-blue-500 to-blue-600';
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      
      // Fetch categories from database
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('categories')
        .select('*')
        .eq('active', true)
        .order('sort_order', { ascending: true });

      if (categoriesError) {
        console.error('Error fetching categories:', categoriesError);
        throw categoriesError;
      }

      setCategories(categoriesData || []);

      // Fetch product counts using category_id OR category (slug)
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select('category_id, category')
        .eq('active', true);

      if (productsError) {
        console.error('Error fetching products:', productsError);
        throw productsError;
      }

      // Count products by category ID or slug
      const counts: { [key: string]: number } = {};
      
      categoriesData?.forEach((cat) => {
        const matchingProducts = productsData?.filter((product) => 
          product.category_id === cat.id || product.category === cat.slug
        );
        counts[cat.slug] = matchingProducts?.length || 0;
      });

      setCategoryCounts(counts);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (categorySlug: string) => {
    navigate(`/category/${encodeURIComponent(categorySlug)}`);
  };

  const getTranslatedName = (category: Category) => {
    // If category has name_translations, use them
    if (category.name_translations && typeof category.name_translations === 'object') {
      const translations = category.name_translations as Record<string, string>;
      
      // Try to get translation for current language
      if (translations[language]) {
        return translations[language];
      }
      
      // Fallback to French
      if (translations.fr) {
        return translations.fr;
      }
    }
    
    // Try to get translation from useLanguage hook (legacy support)
    const translationKey = `categories.${category.slug}`;
    const translated = t(translationKey);
    
    // If no translation found or same as key, return original name
    if (translated === translationKey) {
      return category.name;
    }
    
    return translated;
  };

  if (loading) {
    return (
      <section className="py-16 bg-muted/30">
        <div className="container">
          <div className="text-center mb-12">
            <div className="h-10 bg-muted rounded w-64 mx-auto mb-4 animate-pulse"></div>
            <div className="h-6 bg-muted rounded w-96 mx-auto animate-pulse"></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-40 bg-muted rounded animate-pulse"></div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-muted/30">
      <div className="container">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {t('categories.title')}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('categories.description')}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {categories.map((category) => {
            const IconComponent = getIconComponent(category.icon);
            const count = categoryCounts[category.slug] || 0;
            const bgColor = getColorForCategory(category);
            
            return (
              <Card 
                key={category.id} 
                className="group cursor-pointer hover:shadow-elegant transition-all duration-300 hover:-translate-y-1 bg-gradient-card border-0"
                onClick={() => handleCategoryClick(category.slug)}
              >
                <CardContent className="p-6 text-center">
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${bgColor} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                    <IconComponent className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 group-hover:text-primary transition-colors">
                    {getTranslatedName(category)}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {count} {count === 1 ? t('categories.product', 'produit') : t('categories.products', 'produits')}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CategoriesSection;